// Filter by date range
export function filterByDateRange(data, startDate, endDate, dateField = 'date') {
    if (!data || !Array.isArray(data)) return [];
    if (!startDate && !endDate) return data;

    return data.filter(item => {
        const itemDate = item[dateField];
        if (!itemDate) return false;

        if (startDate && itemDate < startDate) return false;
        if (endDate && itemDate > endDate) return false;

        return true;
    });
}

// Filter by city
export function filterByCity(data, cityIds, cityField = 'cityId') {
    if (!data || !Array.isArray(data)) return [];
    if (!cityIds || cityIds.length === 0) return data;

    return data.filter(item => cityIds.includes(item[cityField]));
}

// Filter by store
export function filterByStore(data, storeIds, storeField = 'storeId') {
    if (!data || !Array.isArray(data)) return [];
    if (!storeIds || storeIds.length === 0) return data;

    return data.filter(item => storeIds.includes(item[storeField]));
}

// Filter by pincode
export function filterByPincode(data, pincodes, pincodeField = 'pincode') {
    if (!data || !Array.isArray(data)) return [];
    if (!pincodes || pincodes.length === 0) return data;

    return data.filter(item => pincodes.includes(item[pincodeField]));
}

// Filter by category
export function filterByCategory(data, categoryIds) {
    if (!data || !Array.isArray(data)) return [];
    if (!categoryIds || categoryIds.length === 0) return data;

    return data.filter(item => {
        // Check direct categoryId
        if (item.categoryId && categoryIds.includes(item.categoryId)) return true;

        // Check items array (for orders)
        if (item.items && Array.isArray(item.items)) {
            return item.items.some(i => categoryIds.includes(i.categoryId));
        }

        return false;
    });
}

// Filter by status
export function filterByStatus(data, statuses, statusField = 'status') {
    if (!data || !Array.isArray(data)) return [];
    if (!statuses || statuses.length === 0) return data;

    return data.filter(item => statuses.includes(item[statusField]));
}

// Filter by payment method
export function filterByPaymentMethod(data, methods) {
    if (!data || !Array.isArray(data)) return [];
    if (!methods || methods.length === 0) return data;

    return data.filter(item => methods.includes(item.paymentMethod));
}

// Apply multi-level filters
export function applyMultiLevelFilters(data, filters = {}) {
    if (!data || !Array.isArray(data)) return [];

    const {
        startDate,
        endDate,
        cities,
        stores,
        pincodes,
        categories,
        statuses,
        paymentMethods,
        searchQuery,
    } = filters;

    let result = [...data];

    // Apply date filter
    if (startDate || endDate) {
        result = filterByDateRange(result, startDate, endDate);
    }

    // Apply city filter
    if (cities && cities.length > 0) {
        result = filterByCity(result, cities);
    }

    // Apply store filter
    if (stores && stores.length > 0) {
        result = filterByStore(result, stores);
    }

    // Apply pincode filter
    if (pincodes && pincodes.length > 0) {
        result = filterByPincode(result, pincodes);
    }

    // Apply category filter
    if (categories && categories.length > 0) {
        result = filterByCategory(result, categories);
    }

    // Apply status filter
    if (statuses && statuses.length > 0) {
        result = filterByStatus(result, statuses);
    }

    // Apply payment method filter
    if (paymentMethods && paymentMethods.length > 0) {
        result = filterByPaymentMethod(result, paymentMethods);
    }

    // Apply search
    if (searchQuery) {
        result = searchData(result, searchQuery);
    }

    return result;
}

// Search data across multiple fields
export function searchData(data, query, fields = ['id', 'customerName', 'storeName']) {
    if (!data || !Array.isArray(data)) return [];
    if (!query || query.trim() === '') return data;

    const searchTerm = query.toLowerCase().trim();

    return data.filter(item => {
        return fields.some(field => {
            const value = item[field];
            if (!value) return false;
            return String(value).toLowerCase().includes(searchTerm);
        });
    });
}

// Filter by numeric range
export function filterByRange(data, field, min, max) {
    if (!data || !Array.isArray(data)) return [];

    return data.filter(item => {
        const value = Number(item[field]);
        if (isNaN(value)) return false;
        if (min !== undefined && value < min) return false;
        if (max !== undefined && value > max) return false;
        return true;
    });
}

// Get filter options from data
export function getFilterOptions(data, field) {
    if (!data || !Array.isArray(data)) return [];

    const unique = [...new Set(data.map(item => item[field]).filter(Boolean))];

    return unique.map(value => ({
        value,
        label: value,
        count: data.filter(item => item[field] === value).length,
    })).sort((a, b) => b.count - a.count);
}

export default {
    filterByDateRange,
    filterByCity,
    filterByStore,
    filterByPincode,
    filterByCategory,
    filterByStatus,
    filterByPaymentMethod,
    applyMultiLevelFilters,
    searchData,
    filterByRange,
    getFilterOptions,
};
